import { myLocalStorage } from "../../StorageHelper";
import { fetchAWSAccountList, fetchAWSTenantAccountList, getAWSAllGroups, getAWSAllUsers } from "./Api/awsapi";
import { fetchAzureSubscriptionList, getAllAzureGroupsAndRoles, getAllAzureUsers } from "./Api/azureapi";
import { fetchGcpprojectList, getGCPAllUsers, getGCPAllUsersWithRoles } from "./Api/gcpapi";
import { AWSAccount, AzureSubscription, CloudUsers } from "./interface";


export const gcpProjectList = async (): Promise<string[]> => {
    const latestTenant = myLocalStorage.getItem("latestTenant")?.tenantName;
    const response = await fetchGcpprojectList(latestTenant);
    return response;

}
export const azureSubcriptionList = async (): Promise<AzureSubscription[]> => {
    const latestTenant = myLocalStorage.getItem("latestTenant")?.tenantName;
    const response = await fetchAzureSubscriptionList(latestTenant);
    return response;
}
export const awsAccountList = async (tenantEmail: string): Promise<AWSAccount[]> => {
    const latestTenant = myLocalStorage.getItem("latestTenant")?.tenantName;
    const response = await fetchAWSAccountList(latestTenant);
    return response;
}
export const awsTenantAccountList = async (tenantEmail: string): Promise<AWSAccount[]> => {
    const latestTenant = myLocalStorage.getItem("latestTenant")?.tenantName;
    const response = await fetchAWSTenantAccountList(latestTenant, tenantEmail);

    return (response || []).map((item: any, index: number) => ({
        id: item.id ?? index,
        accountId: item.accountId || item.awsAccountId || "",
        accountName: item.accountName || item.awsAccountName || "",
        email: item.email || item.tenantEmail || "",
        organizationId: item.organizationId || "",
        tenantName: item.tenantName || latestTenant || "",
        status: item.status || "",
        accountType: item.accountType || "",
        joinedMethod: item.joinedMethod || "",
        joinedTimestamp: item.joinedTimestamp || "",
        roleArn: item.roleArn || item.awsAccountARN || "",
    }));
}


const fetchAWSUser = async () => {
    const latestTenant = myLocalStorage.getItem("latestTenant")?.tenantName;
    const selectedAwsAccount = myLocalStorage.getItem("selectedAwsAccount");
    const [users, groups] = await Promise.all([
        getAWSAllUsers(latestTenant, selectedAwsAccount),
        getAWSAllGroups(latestTenant, selectedAwsAccount)
    ]);
    return { users, groups }
}

const fetchGCPUser = async () => {
    const latestTenant = myLocalStorage.getItem("latestTenant")?.tenantName;
    const latestprojectId = myLocalStorage.getItem("gcpSelectedProjectId");

    const [users, userRoles] = await Promise.all([
        getGCPAllUsers(latestTenant, latestprojectId),
        getGCPAllUsersWithRoles(latestTenant, latestprojectId),
    ]);
    return { users, userRoles }
}

const fetchAzureUser = async () => {
    const latestTenant = myLocalStorage.getItem("latestTenant")?.tenantName;
    const selectedSubListStr = myLocalStorage.getItem("latestSubList");
    const selectedSubList = selectedSubListStr ? JSON.parse(selectedSubListStr) : null;
    let subId = selectedSubList?.azureSubscriptionId;

    const [users, groupsAndRoles] = await Promise.all([
        getAllAzureUsers(latestTenant, subId),
        getAllAzureGroupsAndRoles(latestTenant, subId),
    ]);
    return { users, groupsAndRoles }
}


export const getGCPUsersList = async (): Promise<CloudUsers[]> => {
    const { users, userRoles } = await fetchGCPUser();
    const result: CloudUsers[] = [];

    const usersArray = Array.isArray(users)
        ? users
        : Object.values(users || {}).flatMap((item: any) =>
            Array.isArray(item) ? item : item ? [item] : []
        );

    usersArray.forEach((user: any, index: number) => {
        const email = user.email;
        const rolesFromRolesAPI =
            userRoles[email]?.map((r: any) => r.roleName) || [];
        const roles =
            rolesFromRolesAPI.length > 0 ? rolesFromRolesAPI : user.roles || [];

        result.push({
            id: `gcp-user-${user.id ?? index + 1}`,
            name: user.userName || "-",
            email,
            roles,
            isDormant: user.isDormant ?? false,
            lastActivityMessage: user.lastActivityMessage || "-",
            syncedAt: user.syncedAt
                ? new Date(user.syncedAt).toLocaleString("en-GB", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                })
                : "-",
            activity: Number(user.activity ?? 0),
            groups: [],
            uniqueId: "",
            principalName: "",
            arn: "",
            userDetails: user,
            // Risk & alert fields
            riskChangeCategory: user.riskChangeCategory ?? null,
            alertCount: user.alertCount ?? 0,
            alertCountDelta: user.alertCountDelta ?? 0,
            criticalAlertCount: user.criticalAlertCount ?? 0,
            criticalAlertCountDelta: user.criticalAlertCountDelta ?? 0,
            highAlertCount: user.highAlertCount ?? 0,
            highAlertCountDelta: user.highAlertCountDelta ?? 0,
            highPrivilegeRoleCount: user.highPrivilegeRoleCount ?? 0,
            highPrivilegeRoleCountDelta: user.highPrivilegeRoleCountDelta ?? 0,
            riskTags: user.riskTags ?? [],
            riskComparedAt: user.riskComparedAt ?? "",
        });
    });

    return result;
};


export const getAWSUsersList = async (): Promise<CloudUsers[]> => {
    const response = await fetchAWSUser();
    const users = Array.isArray(response?.users) ? response.users : [];
    const groups = Array.isArray(response?.groups) ? response.groups : [];


    const result: CloudUsers[] = [];

    const groupRolesMap: Record<string, string[]> = {};
    groups.forEach((group: any) => {
        const roles = (group.groupPolicies || []).map(
            (policy: any) => policy.policyName
        );
        groupRolesMap[group.groupName] = roles;
    });

    // Step 2: Iterate through all users and merge their roles
    users.forEach((user: any, index: number) => {
        // Roles from user's attached policies
        const userRoles =
            (user.userPolicies || []).map((p: any) => p.policyName) || [];

        // Roles from groups this user belongs to (if any)
        const groupBasedRoles: string[] = [];
        if (user.groups && Array.isArray(user.groups)) {
            user.groups.forEach((g: any) => {
                if (groupRolesMap[g.groupName]) {
                    groupBasedRoles.push(...groupRolesMap[g.groupName]);
                }
            });
        }

        // Combine and remove duplicates
        const allRoles = Array.from(new Set([...userRoles, ...groupBasedRoles]));

        result.push({
            id: `aws-user-${index + 1}`,
            name: user.username || "-",
            email: user.username
                ? `${user.username}@${user.accountId}.aws`
                : "unknown@aws",
            roles: allRoles.length > 0 ? allRoles : ["No Roles Assigned"],
            isDormant: user.isDormant ?? false,
            lastActivityMessage: user.lastActivityMessage || "-",
            syncedAt: user.createDate,
            activity: Number(user.activity ?? 0),
            groups: [],
            uniqueId: user.userId,
            principalName: "",
            arn: user.arn,
            userDetails: user,
            // Risk & alert fields
            riskChangeCategory: user.riskChangeCategory ?? null,
            alertCount: user.alertCount ?? 0,
            alertCountDelta: user.alertCountDelta ?? 0,
            criticalAlertCount: user.criticalAlertCount ?? 0,
            criticalAlertCountDelta: user.criticalAlertCountDelta ?? 0,
            highAlertCount: user.highAlertCount ?? 0,
            highAlertCountDelta: user.highAlertCountDelta ?? 0,
            highPrivilegeRoleCount: user.highPrivilegeRoleCount ?? 0,
            highPrivilegeRoleCountDelta: user.highPrivilegeRoleCountDelta ?? 0,
            riskTags: user.riskTags ?? [],
            riskComparedAt: user.riskComparedAt ?? "",
        });
    });

    return result;
};

export const getAzureUsersList = async (): Promise<CloudUsers[]> => {
    const { users = [], groupsAndRoles = [] } = await fetchAzureUser();

    const result: CloudUsers[] = [];

    // Combine users and role/group mappings
    const combinedData = users.map((user: any) => {
        const roleGroup = groupsAndRoles.find((gr: any) => gr.azureUserId === user.azureId) || {};

        const sanitizedGroups = (roleGroup.groups || []).map((g: any) =>
            g === "null" ? null : g
        );
        const sanitizedRoles = (roleGroup.roleDefinitions || []).map((r: any) =>
            r === "null" ? null : r
        );

        return {
            ...roleGroup,
            ...user,
            groups: sanitizedGroups,
            roleDefinitions: sanitizedRoles,
        };
    });

    // Now build final unified user list
    combinedData.forEach((item: any, index: number) => {
        const syncedAt = item.syncedAt
            ? new Date(item.syncedAt).toLocaleString("en-GB", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
            })
            : "-";

        result.push({
            id: `azure-user-${index + 1}`,
            uniqueId: item.azureUserId || `azure-user-${index + 1}`,
            name:
                item.displayName ||
                item.userPrincipalName.trim() ||
                "-",
            principalName: item.displayName || item.userPrincipalName || "-",
            email: item.mail || item.userPrincipalName || "-",
            groups: item.groups || [],
            roles: item.roleDefinitions || [],
            isDormant: item.isDormant ?? false,
            lastActivityMessage: item.lastActivityMessage || "-",
            syncedAt,
            activity: Number(item.activity ?? 0),
            arn: "",
            userDetails: item,
            // Risk & alert fields
            riskChangeCategory: item.riskChangeCategory ?? null,
            alertCount: item.alertCount ?? 0,
            alertCountDelta: item.alertCountDelta ?? 0,
            criticalAlertCount: item.criticalAlertCount ?? 0,
            criticalAlertCountDelta: item.criticalAlertCountDelta ?? 0,
            highAlertCount: item.highAlertCount ?? 0,
            highAlertCountDelta: item.highAlertCountDelta ?? 0,
            highPrivilegeRoleCount: item.highPrivilegeRoleCount ?? 0,
            highPrivilegeRoleCountDelta: item.highPrivilegeRoleCountDelta ?? 0,
            riskTags: item.riskTags ?? [],
            riskComparedAt: item.riskComparedAt ?? "",
        });
    });

    return result;
};
